import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import maplibregl, { Map as MlMap, Marker, NavigationControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { HeatmapLayer, type HeatmapStyle } from '../map/HeatmapLayer';
import { fetchLines } from '../lib/api';
import { lonLatToCell } from '../lib/grid';
import {
  MODE_DOT,
  SBAHN_CASING,
  SBAHN_GREEN,
  colorForMinutes,
  formatMinutes,
  modeShorts,
  primaryMode,
} from '../lib/palette';
import type { FeatureCollection } from 'geojson';
import type { GridSpec, Point, Stations, ViewMode } from '../lib/types';

/** Drawn bus-first so the rail dots land on top where they share a station. */
const STOP_LAYERS = ['stops-bus', 'stops'];

const RETINA = typeof window !== 'undefined' && window.devicePixelRatio > 1.25;
const SUFFIX = RETINA ? '@2x' : '';
const CARTO_ATTRIB =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, ' +
  '© <a href="https://carto.com/attributions">CARTO</a> · timetable © ' +
  '<a href="https://www.vbb.de/vbb-services/api-open-data/">VBB</a>';

// Set VITE_CARTO_API_KEY in .env at the repo root — see .env.example.
const CARTO_API_KEY = import.meta.env.VITE_CARTO_API_KEY ?? '';

if (!CARTO_API_KEY) {
  console.warn(
    'VITE_CARTO_API_KEY is not set, so the basemap tiles will fail to load. ' +
      'Copy .env.example to .env, add your CARTO key, and restart the dev server.'
  );
}

function tileUrls(variant: string): string[] {
  const query = CARTO_API_KEY ? `?key=${encodeURIComponent(CARTO_API_KEY)}` : '';
  return ['a', 'b', 'c', 'd'].map(
    (s) => `https://${s}.basemaps.cartocdn.com/${variant}/{z}/{x}/{y}${SUFFIX}.png${query}`
  );
}

function baseStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      'carto-base': {
        type: 'raster',
        tiles: tileUrls('dark_nolabels'),
        tileSize: 256,
        maxzoom: 19,
        attribution: CARTO_ATTRIB,
      },
      'carto-labels': {
        type: 'raster',
        tiles: tileUrls('dark_only_labels'),
        tileSize: 256,
        maxzoom: 19,
      },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#0d0d0d' } },
      { id: 'carto-base', type: 'raster', source: 'carto-base', paint: { 'raster-opacity': 0.95 } },
      // Kept last so street names stay legible on top of the heatmap.
      { id: 'carto-labels', type: 'raster', source: 'carto-labels' },
    ],
  };
}

export interface MapViewHandle {
  setField: (data: Uint16Array, width: number, height: number) => void;
  clearField: () => void;
  flyTo: (lat: number, lon: number, zoom?: number) => void;
}

interface Props {
  grid: GridSpec;
  stations: Stations | null;
  points: Point[];
  viewMode: ViewMode;
  /** Minutes per grid cell (NaN where unreachable), for the cursor readout. */
  values: Float32Array | null;
  heatmapStyle: HeatmapStyle;
  showStops: boolean;
  showLines: boolean;
  pickingMode: 'add' | 'explore';
  onMapClick: (lat: number, lon: number) => void;
  onPointClick: (id: string) => void;
  onPointMoved: (id: string, lat: number, lon: number) => void;
}

export const MapView = forwardRef<MapViewHandle, Props>(function MapView(
  {
    grid,
    stations,
    points,
    viewMode,
    values,
    heatmapStyle,
    showStops,
    showLines,
    pickingMode,
    onMapClick,
    onPointClick,
    onPointMoved,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const layerRef = useRef<HeatmapLayer | null>(null);
  const markersRef = useRef(new Map<string, { marker: Marker; el: HTMLDivElement }>());
  const readoutRef = useRef<HTMLDivElement | null>(null);

  // Latest values for the pointer readout, without re-rendering on mousemove.
  const valuesRef = useRef<Float32Array | null>(null);
  const gridRef = useRef(grid);
  valuesRef.current = values;
  gridRef.current = grid;

  const handlersRef = useRef({ onMapClick, onPointClick, onPointMoved });
  handlersRef.current = { onMapClick, onPointClick, onPointMoved };

  const showLinesRef = useRef(showLines);
  showLinesRef.current = showLines;

  const setLinesVisible = useCallback((visible: boolean) => {
    const map = mapRef.current;
    if (!map) return;
    for (const id of ['sbahn-casing', 'sbahn-line']) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      setField: (data, width, height) => layerRef.current?.setField(data, width, height),
      clearField: () => layerRef.current?.clearField(),
      flyTo: (lat, lon, zoom) =>
        mapRef.current?.flyTo({ center: [lon, lat], zoom: zoom ?? Math.max(mapRef.current.getZoom(), 12.5) }),
    }),
    []
  );

  // ---- map setup (once)
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      center: [13.405, 52.52],
      zoom: 10.2,
      minZoom: 9,
      maxZoom: 17,
      maxBounds: [
        [grid.bbox.minLon - 0.35, grid.bbox.minLat - 0.25],
        [grid.bbox.maxLon + 0.35, grid.bbox.maxLat + 0.25],
      ],
      attributionControl: { compact: true },
      dragRotate: false,
      pitchWithRotate: false,
    });
    mapRef.current = map;
    map.touchZoomRotate.disableRotation();
    map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');

    const layer = new HeatmapLayer(gridRef.current);
    layerRef.current = layer;

    map.on('load', () => {
      map.addLayer(layer, 'carto-labels');
    });
    map.on('error', (e) => console.error('maplibre error:', e.error?.message ?? e));

    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__lowcation = { map, layer };
    }

    map.on('click', (e) => {
      handlersRef.current.onMapClick(e.lngLat.lat, e.lngLat.lng);
    });

    // Which stop dot the cursor is over, so it can be swelled while hovered.
    let hovered: number | null = null;
    const setHovered = (id: number | null) => {
      if (id === hovered) return;
      if (hovered !== null) map.setFeatureState({ source: 'stops', id: hovered }, { hover: false });
      hovered = id;
      if (id !== null) map.setFeatureState({ source: 'stops', id }, { hover: true });
    };

    const onMove = (e: maplibregl.MapMouseEvent) => {
      const el = readoutRef.current;
      if (!el) return;
      const pt = e.point;

      // A few pixels of slop: the dots are small and the cursor is not precise.
      const layers = STOP_LAYERS.filter((id) => map.getLayer(id));
      const hits = layers.length
        ? map.queryRenderedFeatures(
            [
              [pt.x - 6, pt.y - 6],
              [pt.x + 6, pt.y + 6],
            ],
            { layers }
          )
        : [];
      // Rail wins a tie: at a station the bus dot sits underneath.
      const hit = hits.find((f) => f.layer.id === 'stops') ?? hits[0];
      setHovered(hit ? (hit.id as number) : null);

      const vals = valuesRef.current;
      const g = gridRef.current;
      let minutes = NaN;
      let inGrid = false;
      if (vals) {
        const { col, row } = lonLatToCell(g, e.lngLat.lng, e.lngLat.lat);
        const c = Math.floor(col);
        const r = Math.floor(row);
        if (c >= 0 && r >= 0 && c < g.width && r < g.height) {
          inGrid = true;
          minutes = vals[r * g.width + c];
        }
      }

      // Nothing under the cursor at all — no field, no stop.
      if (!inGrid && !hit) {
        el.style.opacity = '0';
        return;
      }

      const reachable = inGrid && Number.isFinite(minutes);
      const stopHtml = hit
        ? `<span class="readout__stop">${modeBadges(Number(hit.properties.modes))}` +
          `${escapeHtml(String(hit.properties.name))}</span>`
        : '';
      const timeHtml = inGrid
        ? `<span class="readout__time">${reachable ? formatMinutes(minutes) : 'out of reach'}</span>`
        : '';
      el.innerHTML = stopHtml + timeHtml;
      el.style.transform = `translate(${pt.x}px, ${pt.y}px)`;
      el.style.opacity = '1';
      el.style.borderColor = reachable
        ? colorForMinutes(minutes)
        : hit
          ? MODE_DOT[primaryMode(Number(hit.properties.modes)) as keyof typeof MODE_DOT]
          : 'rgba(255,255,255,0.16)';
      el.classList.toggle('readout--none', inGrid && !reachable);
    };
    map.on('mousemove', onMove);
    map.on('mouseout', () => {
      setHovered(null);
      if (readoutRef.current) readoutRef.current.style.opacity = '0';
    });

    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current.clear();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Transit stop dots, for context. Two layers off one source: 3,100 of the
   * 3,940 stops are bus-only, so drawn at one size they'd be a grey rash over
   * the whole city. Bus gets small dots that only appear once you're zoomed in
   * far enough for them to separate; rail and tram carry the picture above that.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !stations) return;
    const add = () => {
      if (map.getSource('stops')) return;
      map.addSource('stops', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: stations.lat.map((lat, i) => ({
            type: 'Feature' as const,
            // Numeric ids so hover can be driven through feature state.
            id: i,
            geometry: { type: 'Point' as const, coordinates: [stations.lon[i], lat] },
            properties: {
              name: stations.name[i],
              modes: stations.modes[i],
              mode: primaryMode(stations.modes[i]),
            },
          })),
        },
      });
      /*
       * Hover swells the dot rather than recolouring it — the colour is already
       * carrying the mode. The multiplier has to live inside the interpolate
       * stops, not wrap the whole thing: a "zoom" expression is only legal as
       * the input to a top-level step/interpolate.
       */
      const swell = (r: number): maplibregl.ExpressionSpecification => [
        '*',
        r,
        ['case', ['boolean', ['feature-state', 'hover'], false], 1.45, 1],
      ];
      map.addLayer(
        {
          id: 'stops-bus',
          type: 'circle',
          source: 'stops',
          filter: ['==', ['get', 'mode'], 'B'],
          minzoom: 13,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, swell(2), 15, swell(3.4), 17, swell(5)],
            'circle-color': MODE_DOT.B,
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0.5, 15, 0.8],
            'circle-stroke-width': 1,
            'circle-stroke-color': 'rgba(255,255,255,0.45)',
          },
        },
        'carto-labels'
      );
      map.addLayer(
        {
          id: 'stops',
          type: 'circle',
          source: 'stops',
          filter: ['!=', ['get', 'mode'], 'B'],
          minzoom: 11,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, swell(2.6), 15, swell(6), 17, swell(8.5)],
            'circle-color': [
              'match',
              ['get', 'mode'],
              'S', MODE_DOT.S,
              'U', MODE_DOT.U,
              'R', MODE_DOT.R,
              'T', MODE_DOT.T,
              'F', MODE_DOT.F,
              MODE_DOT.B,
            ],
            'circle-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0.7, 14, 0.95],
            'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 15, 1.6, 17, 2],
            'circle-stroke-color': 'rgba(255,255,255,0.8)',
          },
        },
        'carto-labels'
      );
    };
    if (map.isStyleLoaded()) add();
    else map.once('load', add);
  }, [stations]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      for (const id of STOP_LAYERS) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', showStops ? 'visible' : 'none');
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [showStops]);

  /*
   * S-Bahn skeleton, to orient yourself on the heatmap. Drawn as one colour
   * rather than per-line: four lines share the Stadtbahn tracks and several more
   * share the north-south tunnel, so per-line colours would just show whichever
   * happened to be drawn last. Declared after the stops effect so these layers
   * end up above the stop dots.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const controller = new AbortController();
    let cancelled = false;

    const add = (data: FeatureCollection) => {
      if (cancelled || !mapRef.current || map.getSource('sbahn')) return;
      map.addSource('sbahn', { type: 'geojson', data });
      map.addLayer(
        {
          id: 'sbahn-casing',
          type: 'line',
          source: 'sbahn',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': SBAHN_CASING,
            'line-opacity': 0.7,
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 3.2, 12, 5.4, 16, 9.5],
          },
        },
        'carto-labels'
      );
      map.addLayer(
        {
          id: 'sbahn-line',
          type: 'line',
          source: 'sbahn',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': SBAHN_GREEN,
            // Slightly translucent: reference geometry, not the subject.
            'line-opacity': 0.88,
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1.3, 12, 2.5, 16, 5],
          },
        },
        'carto-labels'
      );
      // The overlay loads asynchronously, so adopt whatever the toggle says now.
      setLinesVisible(showLinesRef.current);
    };

    fetchLines(controller.signal)
      .then((data) => {
        if (!data.features?.length) return;
        if (map.isStyleLoaded()) add(data);
        else map.once('load', () => add(data));
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') console.warn('S-Bahn overlay unavailable:', err);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setLinesVisible(showLines);
  }, [showLines, setLinesVisible]);

  // ---- style + grid changes
  useEffect(() => {
    layerRef.current?.setStyle(heatmapStyle);
  }, [heatmapStyle]);

  useEffect(() => {
    layerRef.current?.setGrid(grid);
  }, [grid]);

  // ---- cursor affordance
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor = pickingMode === 'explore' ? 'help' : 'crosshair';
  }, [pickingMode]);

  // ---- markers
  const exploringId = viewMode.kind === 'explore' ? viewMode.origin.id : null;
  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set<string>();

    const render = (
      id: string,
      lat: number,
      lon: number,
      label: string,
      index: number | null,
      active: boolean,
      draggable: boolean
    ) => {
      seen.add(id);
      let entry = markersRef.current.get(id);
      if (!entry) {
        const el = document.createElement('div');
        el.className = 'pin';
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          handlersRef.current.onPointClick(id);
        });
        const marker = new Marker({ element: el, draggable, anchor: 'bottom' })
          .setLngLat([lon, lat])
          .addTo(map);
        marker.on('dragend', () => {
          const ll = marker.getLngLat();
          handlersRef.current.onPointMoved(id, ll.lat, ll.lng);
        });
        entry = { marker, el };
        markersRef.current.set(id, entry);
      } else {
        entry.marker.setLngLat([lon, lat]);
      }
      entry.marker.setDraggable(draggable);
      entry.el.classList.toggle('pin--active', active);
      entry.el.classList.toggle('pin--probe', index === null);
      entry.el.title = active ? `${label} — showing travel times from here` : `${label} — click to explore from here`;
      entry.el.innerHTML = `
        <span class="pin__dot">${index === null ? '★' : index + 1}</span>
        <span class="pin__label">${escapeHtml(label)}</span>`;
    };

    points.forEach((p, i) => render(p.id, p.lat, p.lon, p.label, i, p.id === exploringId, true));
    if (viewMode.kind === 'explore' && !points.some((p) => p.id === viewMode.origin.id)) {
      const o = viewMode.origin;
      render(o.id, o.lat, o.lon, o.label, null, true, false);
    }

    for (const [id, entry] of markersRef.current) {
      if (!seen.has(id)) {
        entry.marker.remove();
        markersRef.current.delete(id);
      }
    }
  }, [points, viewMode, exploringId]);

  useEffect(() => {
    syncMarkers();
  }, [syncMarkers]);

  return (
    <div className="map">
      <div className="map__canvas" ref={containerRef} />
      <div className="readout" ref={readoutRef} />
    </div>
  );
});

function modeBadges(modes: number): string {
  return modeShorts(modes)
    .map((m) => `<span class="badge badge--${m.short}">${m.short}</span>`)
    .join('');
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
  );
}
